/**
 * The diagnostics ring buffer (FINISH_PLAN WP4.4, X14): the last log lines, warnings and errors, kept in memory for
 * Settings → Copy diagnostics (diagnostics.ts). No imports, so the Logger (adapters.ts) can feed it without a cycle.
 */

export type DiagLevel = "info" | "warning" | "error";
export interface DiagEntry { readonly atMs: number; readonly level: DiagLevel; readonly message: string }

/** A fixed-size log: the newest `capacity` entries, oldest dropped first. */
export class RingLog {
  private readonly buf: DiagEntry[] = [];
  constructor(readonly capacity: number) {}
  push(e: DiagEntry): void {
    this.buf.push(e);
    if (this.buf.length > this.capacity) this.buf.splice(0, this.buf.length - this.capacity);
  }
  entries(): readonly DiagEntry[] { return this.buf; }
}

/** Enough to cover a session's trouble without the text becoming a wall. */
export const DIAG_CAPACITY = 150;
/** One message is cut here: a stack or a JSON dump must not crowd the rest out. */
export const DIAG_MESSAGE_MAX = 600;

export const diagLog = new RingLog(DIAG_CAPACITY);

/** Records a line (the game's Logger calls this for every line; see adapters.ts `consoleLogger`). */
export function recordDiag(level: DiagLevel, message: string, nowMs: number = Date.now()): void {
  diagLog.push({ atMs: nowMs, level, message: message.length > DIAG_MESSAGE_MAX ? `${message.slice(0, DIAG_MESSAGE_MAX)}…` : message });
}

let installed = false;
/**
 * Starts catching uncaught errors, rejected promises and every console warning and error (the game's Logger writes
 * its warnings and errors to the console too, so they arrive here once). Runs when this module first loads, which is
 * with the adapters, before the save opens.
 */
export function installDiagnostics(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => recordDiag("error", `uncaught: ${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`));
  window.addEventListener("unhandledrejection", (e) => recordDiag("error", `unhandled rejection: ${describe(e.reason)}`));
  for (const [method, level] of [["warn", "warning"], ["error", "error"]] as const) {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => { recordDiag(level, args.map(describe).join(" ")); original(...args); };
  }
}

function describe(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}${v.stack ? `\n${v.stack.split("\n").slice(1, 4).join("\n")}` : ""}`;
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

installDiagnostics();
